import React, { useEffect, useState } from "react";
import {
  Table,
  Button,
  Modal,
  Form,
  Spinner,
  Image,
  Alert,
  Pagination,
} from "react-bootstrap";
import Card from "../../../components/Card";
import {
  getCropTypes,
  createCropType,
  updateCropType,
  deleteCropType,
} from "../../../services/cropService";
import { toast } from "react-toastify";

const CropTypeManager = () => {
  const [crops, setCrops] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [deleteError, setDeleteError] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({
    name: { en: "", hi: "" },
    description: { en: "", hi: "" },
    image: null,
    imagePreview: "",
  });
  const [modalError, setModalError] = useState("");

  // Pagination and Search state
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [totalRecords, setTotalRecords] = useState(0);
  const recordsPerPage = 10;
  const totalPages = Math.ceil(totalRecords / recordsPerPage);

  // Fetch crops
  const fetchCrops = async (page = currentPage, search = searchTerm) => {
    setLoading(true);
    setError("");
    try {
      const response = await getCropTypes({
        page,
        limit: recordsPerPage,
        search: search.trim(),
      });

      // API returns { data: [], pagination: { total: ... } }
      if (response && Array.isArray(response.data)) {
        setCrops(response.data);
        setTotalRecords(response.pagination?.total || 0);
      }
    } catch (err) {
      setError("Failed to load crops");
    } finally {
      setLoading(false);
    }
  };

  // useEffect(() => {
  //   fetchCrops(currentPage, searchTerm);
  // }, [currentPage]);

  // Debounced search
  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      if (currentPage !== 1) {
        setCurrentPage(1);
      } else {
        fetchCrops(1, searchTerm);
      }
    }, 500);

    return () => clearTimeout(delayDebounceFn);
  }, [searchTerm]);

  // Show modal for add/edit
  const handleShowModal = (crop = null) => {
    setModalError("");
    if (crop) {
      setEditingId(crop._id);
      setForm({
        name: { en: crop.name?.en || "", hi: crop.name?.hi || "" },
        description: {
          en: crop.description?.en || "",
          hi: crop.description?.hi || "",
        },
        image: null,
        imagePreview: crop.image || "",
      });
    } else {
      setEditingId(null);
      setForm({
        name: { en: "", hi: "" },
        description: { en: "", hi: "" },
        image: null,
        imagePreview: "",
      });
    }
    setShowModal(true);
  };

  // Show delete modal
  const handleShowDeleteModal = (id) => {
    setDeleteId(id);
    setDeleteError("");
    setShowDeleteModal(true);
  };

  // Delete crop
  const confirmDelete = async () => {
    if (!deleteId) return;
    setLoading(true);
    try {
      await deleteCropType(deleteId);
      fetchCrops();
      setShowDeleteModal(false);
      setDeleteId(null);
      toast.success("Crop deleted successfully!");
    } catch (err) {
      const msg = err?.response?.data?.message || "Failed to delete crop type";
      setDeleteError(msg);
      toast.error("msg");
    } finally {
      setLoading(false);
    }
  };

  // Save crop (create/update)
  const handleSave = async (e) => {
    e.preventDefault();

    if (!form.name.en.trim()) {
      setModalError("Please enter crop name (English)");
      return;
    }
    if (!form.image && !editingId) {
      setModalError("Please select an image");
      return;
    }

    setLoading(true);
    setModalError("");
    try {
      const formData = new FormData();
      formData.append("name.en", form.name.en.trim());
      formData.append("name.hi", form.name.hi.trim());
      formData.append("description.en", form.description.en.trim());
      formData.append("description.hi", form.description.hi.trim());
      if (form.image) formData.append("image", form.image);

      if (editingId) {
        await updateCropType(editingId, formData);
        toast.success("Crop Updated successfully!");
      } else {
        await createCropType(formData);
        toast.success("Crop Created successfully!");
      }

      fetchCrops();
      setShowModal(false);
      setEditingId(null);
      setForm({
        name: { en: "", hi: "" },
        description: { en: "", hi: "" },
        image: null,
        imagePreview: "",
      });
    } catch (err) {
      const rawMessage =
        err?.response?.data?.message || "Failed to save crop type";
      const cleanedMessage = rawMessage.replace(/[\\/"]+/g, "");
      setModalError(cleanedMessage);
      toast.error(cleanedMessage);
    } finally {
      setLoading(false);
    }
  };

  const TruncatedText = ({ text, limit = 40 }) => {
    const [expanded, setExpanded] = useState(false);
    if (!text) return <span>-</span>;
    const isLong = text.length > limit;
    const displayText =
      expanded || !isLong ? text : text.substring(0, limit) + "...";

    return (
      <div style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
        {displayText}{" "}
        {isLong && (
          <Button
            variant="link"
            size="sm"
            className="p-0"
            onClick={() => setExpanded(!expanded)}
          >
            {expanded ? "Read less" : "Read more"}
          </Button>
        )}
      </div>
    );
  };

  // Paginated data (now handled by API)
  const paginatedCrops = crops;

  return (
    <Card className="mb-4">
      <Card.Header>
        <Card.Header.Title>Crop Types</Card.Header.Title>
        <Card.Header.Action className="d-flex align-items-center">
          <Form.Control
            type="text"
            placeholder="Search crops..."
            size="sm"
            className="me-3"
            style={{ width: "200px" }}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <Button variant="primary" size="sm" onClick={() => handleShowModal()}>
            Add Crop
          </Button>
        </Card.Header.Action>
      </Card.Header>
      <Card.Body>
        {error && <Alert variant="danger">{error}</Alert>}
        <Table striped hover responsive className="mb-0">
          <thead>
            <tr>
              <th>Name</th>
              <th>Image</th>
              <th>Description</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={4} className="text-center">
                  <Spinner animation="border" size="sm" />
                </td>
              </tr>
            ) : paginatedCrops.length === 0 ? (
              <tr>
                <td colSpan={4} className="text-center">
                  No crop types found
                </td>
              </tr>
            ) : (
              paginatedCrops.map((crop) => (
                <tr key={crop._id}>
                  <td>
                    {(() => {
                      const name = crop.name;
                      const hasAny =
                        name?.en ||
                        name?.hi ||
                        (typeof name === "string" && name);

                      return hasAny ? (
                        <>
                          {typeof name === "string" && <div>{name}</div>}
                          {name?.en && <div>{name.en}</div>}
                          {name?.hi && (
                            <div className="text-muted small">{name.hi}</div>
                          )}
                        </>
                      ) : (
                        <div>-</div>
                      );
                    })()}
                  </td>

                  <td>
                    {crop.image ? (
                      <Image src={crop.image} width={50} thumbnail />
                    ) : (
                      "-"
                    )}
                  </td>

                  <td>
                    {(() => {
                      const desc = crop.description;
                      const hasAny =
                        desc?.en ||
                        desc?.hi ||
                        (typeof desc === "string" && desc);

                      return hasAny ? (
                        <>
                          {typeof desc === "string" && (
                            <TruncatedText text={desc} limit={80} />
                          )}
                          {desc?.en && (
                            <TruncatedText text={desc.en} limit={80} />
                          )}
                          {desc?.hi && (
                            <div className="text-muted small">
                              <TruncatedText text={desc.hi} limit={80} />
                            </div>
                          )}
                        </>
                      ) : (
                        <div>-</div>
                      );
                    })()}
                  </td>
                  <td>
                    <Button
                      size="sm"
                      variant="success"
                      className="me-2"
                      onClick={() => handleShowModal(crop)}
                    >
                      Edit
                    </Button>
                    <Button
                      size="sm"
                      variant="danger"
                      onClick={() => handleShowDeleteModal(crop._id)}
                    >
                      Delete
                    </Button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </Table>

        {/* Pagination */}
        {totalPages > 1 && (
          <Pagination className="mt-3 justify-content-center">
            <Pagination.First
              onClick={() => setCurrentPage(1)}
              disabled={currentPage === 1}
            />
            <Pagination.Prev
              onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
            />

            {/* Show first page if not in window */}
            {currentPage > 3 && (
              <>
                <Pagination.Item onClick={() => setCurrentPage(1)}>
                  1
                </Pagination.Item>
                {currentPage > 4 && <Pagination.Ellipsis disabled />}
              </>
            )}

            {/* Window of pages around current page */}
            {[...Array(totalPages)].map((_, i) => {
              const page = i + 1;
              if (page >= currentPage - 2 && page <= currentPage + 2) {
                return (
                  <Pagination.Item
                    key={page}
                    active={currentPage === page}
                    onClick={() => setCurrentPage(page)}
                  >
                    {page}
                  </Pagination.Item>
                );
              }
              return null;
            })}

            {/* Show last page if not in window */}
            {currentPage < totalPages - 2 && (
              <>
                {currentPage < totalPages - 3 && (
                  <Pagination.Ellipsis disabled />
                )}
                <Pagination.Item onClick={() => setCurrentPage(totalPages)}>
                  {totalPages}
                </Pagination.Item>
              </>
            )}

            <Pagination.Next
              onClick={() =>
                setCurrentPage((prev) => Math.min(prev + 1, totalPages))
              }
              disabled={currentPage === totalPages}
            />
            <Pagination.Last
              onClick={() => setCurrentPage(totalPages)}
              disabled={currentPage === totalPages}
            />
          </Pagination>
        )}
      </Card.Body>

      {/* Add/Edit Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>
            {editingId ? "Edit Crop Type" : "Add Crop Type"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {modalError && <div className="text-danger mb-3">{modalError}</div>}
          <Form onSubmit={handleSave}>
            <Form.Group className="mb-3">
              <Form.Label>
                Name (English) <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="text"
                value={form.name.en}
                onChange={(e) =>
                  setForm({
                    ...form,
                    name: { ...form.name, en: e.target.value },
                  })
                }
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Name (Hindi)</Form.Label>
              <Form.Control
                type="text"
                value={form.name.hi}
                onChange={(e) =>
                  setForm({
                    ...form,
                    name: { ...form.name, hi: e.target.value },
                  })
                }
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Description (English)</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={form.description.en}
                onChange={(e) =>
                  setForm({
                    ...form,
                    description: { ...form.description, en: e.target.value },
                  })
                }
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Description (Hindi)</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={form.description.hi}
                onChange={(e) =>
                  setForm({
                    ...form,
                    description: { ...form.description, hi: e.target.value },
                  })
                }
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>
                Image {!editingId && <span className="text-danger">*</span>}
              </Form.Label>
              <Form.Control
                type="file"
                accept="image/*"
                onChange={(e) => {
                  const file = e.target.files[0];
                  if (file) {
                    setForm({
                      ...form,
                      image: file,
                      imagePreview: URL.createObjectURL(file),
                    });
                  }
                }}
                required={!editingId}
              />
              {/* Show preview */}
              {form.imagePreview && (
                <div className="mt-3 text-center">
                  <Image src={form.imagePreview} thumbnail width={120} />
                </div>
              )}
              <Form.Text className="text-muted">
                Please upload file type '.jpg', '.jpeg', '.png', '.webp', and
                max 5 MB
              </Form.Text>
            </Form.Group>

            <div className="d-flex justify-content-end">
              <Button
                variant="secondary"
                className="me-2"
                onClick={() => {
                  setShowModal(false);
                  setEditingId(null);
                  setForm({
                    name: { en: "", hi: "" },
                    description: { en: "", hi: "" },
                    image: null,
                    imagePreview: "",
                  });
                  setModalError("");
                }}
              >
                Cancel
              </Button>
              <Button variant="primary" type="submit" disabled={loading}>
                {loading ? (
                  <Spinner animation="border" size="sm" />
                ) : editingId ? (
                  "Update"
                ) : (
                  "Create"
                )}
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Delete Crop Type</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {deleteError ? (
            <Alert variant="danger">{deleteError}</Alert>
          ) : (
            <p>Are you sure you want to delete this crop type?</p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          {!deleteError && (
            <Button variant="danger" onClick={confirmDelete} disabled={loading}>
              {loading ? <Spinner animation="border" size="sm" /> : "Delete"}
            </Button>
          )}
        </Modal.Footer>
      </Modal>
    </Card>
  );
};

export default CropTypeManager;
